// The FilterCriterion ⇄ SelectedFilterOption adapter. Pure, no React.
//
// The agent create payload persists filters as `FilterCriterion[]` — flat,
// with no operator field (SavedFilters.criteria — see
// features/trend-detection/types.ts). The charts-v2 filter-card machinery
// speaks `SelectedFilterOption[]` — an editable `operator`, numeric
// `min`/`max`. This module is the seam between the two: it never guesses.
// A criterion it can't draw as a card is left out of the selections but stays
// in the `criteria` array the caller owns, so saving never changes what an
// agent matches.
import type { SelectedFilterOption } from 'features/charts-v2/types'
// bully ignore[no-cross-feature-private-imports: charts-v2 has no public seam for a value util (the barrel is banned for runtime cross-feature imports by no-cross-feature-barrel-imports), and this is the single parent-to-child theme expansion the charts picker and Ask Yogi deep-links already use; re-deriving it here would fork the "Parent: Child" naming rule. follow-up: .agents/follow-ups/bully-typescript-cross-feature-hooks.md]
import { expandParentThemeNamesToSubThemeClusterUuids } from 'features/charts-v2/utils/theme-hierarchy'
import { FilterTypes, type FilterOption } from 'features/filters/types'
import type {
  FilterCriterion,
  IssueAgentResponse,
} from 'features/trend-detection/types'

/** The only operator `FilterCriterion` can express — it has no operator
 * field, so the backend transpiler treats every criterion as an implicit
 * equality match. Locked, not defaulted: the UI must never let a user author
 * a filter (e.g. "is not") that the create payload would then silently
 * ignore. */
export const AGENT_FILTER_OPERATOR = 'is' as const

/** Fields whose `SelectedFilterOption` shape has no home in
 * `SavedFilters.criteria` — sentiment (categorical + numeric) and the two
 * numeric record/sentence counters. None of these round-trip through
 * `FilterCriterion`, so the agent picker must never offer them. */
export const UNSUPPORTED_AGENT_FILTER_FIELDS: ReadonlySet<string> = new Set([
  'record_count',
  'sentence_count',
  'sentiment_filter',
  'sentiment',
  'post_sentiment',
])

/** Whether the agent filter picker should offer this option at all. Excludes
 * DATE fields (no editor on `FilterCard` — it renders them as static text —
 * and a date filter would fight the agent's fixed analysis window) and the
 * unsupported fields above. */
export function isAgentFilterableOption(option: FilterOption): boolean {
  if (option.filter_type === FilterTypes.DATE) return false
  return !UNSUPPORTED_AGENT_FILTER_FIELDS.has(option.field)
}

/** Narrows a project's full filter-option catalogue to what the agent create
 * form's picker may show. Apply before handing `filterOptions` to
 * `FilterControl` — this is what keeps DATE/numeric/sentiment fields out of
 * the "+ Filter" dimension list in the first place. */
export function narrowAgentFilterOptions(
  options: readonly FilterOption[],
): FilterOption[] {
  return options.filter(isAgentFilterableOption)
}

/** Wire shape returned by the LLM filter-interpret endpoint — `min`/`max` may
 * arrive as either a string or a number depending on the field. */
/** Legacy saved/LLM-emitted criterion for a whole parent theme. It is not a
 * filter option; the `theme` card owns it as a display alias. */
const PARENT_THEME_FIELD = 'parent_theme'

export type SavedFiltersCriteriaItem = {
  field: string
  values?: string[]
  value?: string | string[]
  min?: string | number
  max?: string | number
}

/** Wire → FilterCriterion. Lifted verbatim from the deleted
 * `use-agent-setup.ts`'s `normalizeCriteria` — coerces numeric min/max to
 * strings and strips nullish fields. Composes with
 * `criteriaToSelectedFilterOptions` below: interpret response →
 * `normalizeInterpretedCriteria` → `FilterCriterion[]` → cards. */
export function normalizeInterpretedCriteria(
  criteria: readonly SavedFiltersCriteriaItem[],
): FilterCriterion[] {
  return criteria.map((c) => {
    const item: FilterCriterion = { field: c.field }
    if (c.values != null) item.values = c.values
    if (c.value != null) item.value = c.value
    if (c.min != null) item.min = String(c.min)
    if (c.max != null) item.max = String(c.max)
    return item
  })
}

/** Runs the text-to-filter LLM interpret call and normalizes its response,
 * retrying once on an empty result. The call silently returns an empty list
 * on a handful of failure paths (no tool call, refusal, transient error);
 * one free retry roughly halves the empty-result rate before the caller
 * accepts it as the user's actual answer. `text` is the prompt to interpret;
 * an empty/whitespace-only prompt short-circuits to no criteria without
 * calling `runInterpret`. `valueLabels` names the theme uuids the response
 * emitted (uuid → theme name), which the filter options may omit. Kept verbatim in spirit from the deleted
 * `use-agent-setup.ts`'s inline retry, lifted out here so
 * `use-agent-create-form.ts` stays a thin hook over this pure async step. */
export async function interpretCriteriaWithRetry(
  runInterpret: (prompt: string) => Promise<{
    criteria: unknown
    value_labels?: Record<string, string> | null
  }>,
  text: string,
): Promise<{
  criteria: FilterCriterion[]
  valueLabels: Record<string, string>
}> {
  if (!text.trim()) return { criteria: [], valueLabels: {} }
  let response = await runInterpret(text)
  let criteria = normalizeInterpretedCriteria(
    response.criteria as SavedFiltersCriteriaItem[],
  )
  if (criteria.length === 0) {
    response = await runInterpret(text)
    criteria = normalizeInterpretedCriteria(
      response.criteria as SavedFiltersCriteriaItem[],
    )
  }
  return { criteria, valueLabels: response.value_labels ?? {} }
}

function toValueArray(value: string | string[] | undefined): string[] | null {
  if (value == null) return null
  return Array.isArray(value) ? value : [value]
}

/** Single-criterion form of `criteriaToSelectedFilterOptions`. Returns `null`
 * when the criterion can't be represented as a `SelectedFilterOption` — the
 * caller leaves it undrawn (see `criteriaToSelectedFilterOptions`).
 *
 * Conversion rules:
 *  - `operator` is always `AGENT_FILTER_OPERATOR` ("is").
 *  - `values ?? toArray(value) ?? []` — when both are present, `values`
 *    wins; it's the only shape the picker itself ever writes, `value`
 *    appears only on the LLM-interpret path.
 *  - `min`/`max` are only meaningful for WINDOW (rating) fields, and only as
 *    NUMBERS. A DATE field's `min`/`max` are ISO date strings —
 *    `Number("2025-01-01T…")` is `NaN`, so those are never coerced; the
 *    criterion is unrepresentable instead.
 *  - A field absent from `filterOptions` is unrepresentable. (`parent_theme`
 *    is handled one level up, by `criteriaToSelectedFilterOptions`.)
 */
export function criterionToSelectedFilterOption(
  criterion: FilterCriterion,
  filterOptions: readonly FilterOption[],
): SelectedFilterOption | null {
  if (UNSUPPORTED_AGENT_FILTER_FIELDS.has(criterion.field)) return null

  const option = filterOptions.find((o) => o.field === criterion.field)
  if (!option) return null

  const values = criterion.values ?? toValueArray(criterion.value) ?? []

  if (option.filter_type === FilterTypes.WINDOW) {
    const min = criterion.min != null ? Number(criterion.min) : undefined
    const max = criterion.max != null ? Number(criterion.max) : undefined
    return {
      field: criterion.field,
      operator: AGENT_FILTER_OPERATOR,
      values,
      ...(min != null && !Number.isNaN(min) ? { min } : {}),
      ...(max != null && !Number.isNaN(max) ? { max } : {}),
    }
  }

  // Non-WINDOW criterion carrying min/max (a DATE criterion, in practice) —
  // unrepresentable rather than guessing a numeric coercion.
  if (criterion.min != null || criterion.max != null) return null

  return { field: criterion.field, operator: AGENT_FILTER_OPERATOR, values }
}

/** `FilterCriterion[]` → what `FilterControl` renders. A criterion that can't
 * map (see `criterionToSelectedFilterOption`) is left out; it stays in the
 * caller's `criteria`, so it is still saved, just not drawn.
 *
 * A legacy `parent_theme` criterion is an alias of the `theme` card: its
 * parent names expand to child cluster uuids against `filterOptions` and are
 * unioned into the single `theme` selection, alongside any `theme` criterion.
 * Until the options carry themes it expands to nothing and is not drawn. */
export function criteriaToSelectedFilterOptions(
  criteria: readonly FilterCriterion[],
  filterOptions: readonly FilterOption[],
): SelectedFilterOption[] {
  const selections: SelectedFilterOption[] = []
  const mergeTheme = (theme: SelectedFilterOption): void => {
    const index = selections.findIndex((s) => s.field === 'theme')
    if (index === -1) {
      selections.push(theme)
      return
    }
    const existing = selections[index]!
    selections[index] = {
      ...existing,
      values: [...new Set([...existing.values, ...theme.values])],
    }
  }
  for (const criterion of criteria) {
    if (criterion.field === PARENT_THEME_FIELD) {
      const values = expandParentThemeNamesToSubThemeClusterUuids(
        criterion.values ?? toValueArray(criterion.value) ?? [],
        [...filterOptions],
      )
      if (values.length > 0) {
        mergeTheme({ field: 'theme', operator: AGENT_FILTER_OPERATOR, values })
      }
      continue
    }
    const selection = criterionToSelectedFilterOption(criterion, filterOptions)
    if (selection?.field === 'theme') mergeTheme(selection)
    else if (selection) selections.push(selection)
  }
  return selections
}

/** Reverse direction: a card's live selection → the criterion the create
 * payload persists. `null` when the selection cannot round-trip (a numeric
 * selection — belt and braces, `UNSUPPORTED_AGENT_FILTER_FIELDS` already
 * keeps these out of the picker).
 *
 * `operator` is discarded — the payload has nowhere to put it.
 * `valueType` (ratings-only/reviews-only) is also dropped: the create
 * payload hardcodes `ratings_type: null` and nothing on the read side
 * renders it, so inventing a mapping would be UI for a value nobody reads. */
export function selectedFilterOptionToCriterion(
  selection: SelectedFilterOption,
): FilterCriterion | null {
  if (selection.numericValue !== undefined) return null
  if (selection.values.length > 0) {
    return { field: selection.field, values: [...selection.values] }
  }
  if (selection.min !== undefined && selection.max !== undefined) {
    return {
      field: selection.field,
      min: String(selection.min),
      max: String(selection.max),
    }
  }
  return null
}

/** The write path behind `FilterControlProps['updateFilterSelection']`.
 * Pure — returns a new array, `criteria` is never mutated. No operator
 * parameter: every write is implicitly `AGENT_FILTER_OPERATOR`.
 *
 * Delete detection copies `features/keywords/store.ts`'s convention
 * verbatim: a call is a delete iff `values` is empty AND no
 * min/max/valueType/numericValue is present in `range` — a WINDOW field
 * sends empty values + `{min,max,valueType}` on an update (not a delete),
 * and a numeric field sends empty values + `{numericValue}` the same way. */
export function applyFilterSelectionToCriteria(
  criteria: readonly FilterCriterion[],
  field: string,
  values: readonly string[],
  range?: {
    min?: number
    max?: number
    valueType?: number
    numericValue?: number
  },
): FilterCriterion[] {
  const isDelete =
    values.length === 0 &&
    range?.numericValue === undefined &&
    range?.min === undefined &&
    range?.max === undefined &&
    range?.valueType === undefined

  // The `theme` card owns any legacy `parent_theme` criterion (see
  // `criteriaToSelectedFilterOptions`), so a theme write or delete clears it.
  const ownedFields =
    field === 'theme'
      ? new Set(['theme', PARENT_THEME_FIELD])
      : new Set([field])

  if (isDelete) return criteria.filter((c) => !ownedFields.has(c.field))

  const next: FilterCriterion =
    range?.min !== undefined && range?.max !== undefined
      ? { field, min: String(range.min), max: String(range.max) }
      : { field, values: [...values] }

  const existingIndex = criteria.findIndex((c) => ownedFields.has(c.field))
  if (existingIndex === -1) return [...criteria, next]

  // Replace in place (not filter+append) so field order in the rendered
  // card list stays stable across an edit.
  return criteria.flatMap((c, i) => {
    if (i === existingIndex) return [next]
    return ownedFields.has(c.field) ? [] : [c]
  })
}

/** Reads the saved `FilterCriterion[]` out of an issue agent's persisted
 * config, for the edit-mode create form's `hydrateFromAgent`. */
export function extractAgentCriteria(
  agent: IssueAgentResponse,
): FilterCriterion[] {
  const raw =
    agent.config?.filters && 'criteria' in agent.config.filters
      ? agent.config.filters.criteria
      : []
  return Array.isArray(raw) ? (raw as FilterCriterion[]) : []
}
